import React, { Ref, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from 'shared/store';
import {
  fetchAllRooms,
  selectAllRoomsWithLiveListen,
  setListenedDialingParticipantIds,
} from 'shared/store/salesfloor-slice';
import {
  fetchActiveDialSessions,
  setJoining,
  LiveListenDialSession,
  setLiveListenDialSessionIds,
} from 'shared/store/livelisten-slice';
import { loadUsers } from 'shared/store/org-slice';
import { VideoRoomParticipant } from 'shared/hooks/video-room';
import { AppState } from 'shared/store/reducers';
import { useDispatch } from 'react-redux';
import { makeLog } from 'shared/utils/log';
import { joinSalesfloorRoom, leaveSalesfloorRoom } from 'shared/api';
import { selectActiveOrgMembers, selectActiveUser } from 'shared/store/auth-slice';
import { message } from 'antd';
import { LBBOutboundContact, User } from 'sb-lib/types';
import { CallTaskStatus, DialTask } from 'types/outbound';
import { SalesfloorRoom } from 'types/salesfloor';
import { LiveListenState } from 'types/livelisten';
import { VideoRoomContext } from 'shared/contexts/video-room';
import { useHasOrgPermission } from 'shared/hooks';
import { VideoParticipantType } from 'types/video';
import { selectCallStatus } from 'shared/store/realtime-slice';

const log = makeLog('LiveListenHook');

export interface LiveListen {
  disconnect: (dialSessionId: number | null, salesfloorRoomId: string | null) => void;
  joinCall: (dialSessionId: number) => void;
  audioRef: Ref<HTMLAudioElement>;
  activeSessions: LiveListenCallData[];
  listenedSession: LiveListenCallData | null;
  joining: boolean;
}

export interface LiveListenCallData {
  agent: User | undefined;
  dialSession: LiveListenDialSession;
  salesfloorRoom: SalesfloorRoom | undefined;
  activeCall: DialTask | undefined | null;
  prospect: LBBOutboundContact | undefined;
  callStartedAt: number | null;
  callDurationSeconds: number | null;
  callStatus: CallTaskStatus | undefined;
}

function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const useLiveListen = (): LiveListen => {
  const dispatch = useDispatch();

  // this is needed to manage daily rooms and streams
  const videoRoomManager = useContext(VideoRoomContext);
  const users = useAppSelector(selectActiveOrgMembers);
  const authedUser = useAppSelector(selectActiveUser);
  const allRooms = useAppSelector(selectAllRoomsWithLiveListen);
  const dialSessions = useAppSelector((state: AppState) => state.livelisten.dialSessions);
  const {
    autoListenState,
    autoListenDelay,
    liveListenDialSessionId,
    salesfloorRoomId,
    joining,
  } = useAppSelector((state: AppState) => state.livelisten);

  const callStatus = useAppSelector(selectCallStatus);

  const { join, leave } = videoRoomManager;
  const hasLiveListenPermission = useHasOrgPermission('liveListen', 'read');

  // This is needed for the various functions to see the correct IDs
  const liveListenDialSessionIdRef = useRef<number | null>(liveListenDialSessionId);
  const salesfloorRoomIdRef = useRef<string | null>(salesfloorRoomId);
  useEffect(() => {
    liveListenDialSessionIdRef.current = liveListenDialSessionId;
    salesfloorRoomIdRef.current = salesfloorRoomId;
  }, [liveListenDialSessionId, salesfloorRoomId]);

  const disconnectCall = () => {
    log.debug(
      `Disconnecting before unmounting LiveListenAgentsTable ${liveListenDialSessionIdRef.current} ${salesfloorRoomIdRef.current}`,
    );
    disconnect(liveListenDialSessionIdRef.current, salesfloorRoomIdRef.current);
  };

  useEffect(() => {
    return disconnectCall;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!liveListenDialSessionId) {
      return;
    }
    /* 
      If a call from website is incoming or active, disconnect from a live listen
    */
    if (['active', 'establishing'].includes(callStatus)) {
      disconnectCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus]);
  // a ref to the audio element to connect the streams to
  const audioRef = React.useRef<HTMLAudioElement>(null);

  // this is needed for auto-joining
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // load some data we need
  useEffect(() => {
    dispatch(loadUsers());
    dispatch(fetchAllRooms());
    dispatch(fetchActiveDialSessions());
  }, [dispatch]);

  const getDialSessionData = useCallback(
    (dialSessionId: number) => {
      return dialSessions
        ? dialSessions.find(dialSession => dialSession.id === dialSessionId)
        : undefined;
    },
    [dialSessions],
  );

  const getSalesfloorRoomWithUserParticipating = useCallback(
    (userId: number) => {
      return allRooms.find(
        room => room.participants.findIndex(p => p.id === `user_${userId}`) >= 0,
      );
    },
    [allRooms],
  );

  /**
   * Get sessions which have an active call, taking into account the delay set.
   */
  const getJoinableSessions = useCallback(() => {
    const now = new Date();
    const timestampUTCNowMs = now.getTime() + now.getTimezoneOffset() * 60 * 1000;

    return dialSessions.filter(dialSession =>
      dialSession.tasksStarted?.find(dialTask => {
        const callDurationInSeconds = Math.floor(
          /* @ts-ignore */
          (timestampUTCNowMs - Date.parse(dialTask?.dateUpdated)) / 1000,
        );
        const salesfloorRoom = getSalesfloorRoomWithUserParticipating(dialSession.userId);
        return (
          dialTask.status === 'answered' &&
          dialSession.liveListenEnabled &&
          !dialSession.ended &&
          callDurationInSeconds >= autoListenDelay &&
          salesfloorRoom
        );
      }),
    );
  }, [dialSessions, getSalesfloorRoomWithUserParticipating, autoListenDelay]);

  const disconnect = useCallback(
    async (dialSessionId: number | null, salesfloorRoomId: string | null) => {
      const sessionToDisconnectId = dialSessionId
        ? dialSessionId
        : liveListenDialSessionIdRef.current;

      if (!sessionToDisconnectId) {
        log.debug(`No session ID to disconnect ${sessionToDisconnectId}`);
        return;
      }

      log.info(`Disconnecting dial session ${sessionToDisconnectId}`);

      try {
        await leave();

        if (salesfloorRoomId) {
          log.info(`Leaving Salesfloor room ${salesfloorRoomId}`);
          await leaveSalesfloorRoom(salesfloorRoomId);
        }
      } catch (e) {
        log.error(e);
      }
      liveListenDialSessionIdRef.current = null;
      salesfloorRoomIdRef.current = null;
      dispatch(
        setLiveListenDialSessionIds({
          liveListenDialSessionId: null,
          salesfloorRoomId: null,
        }),
      );
    },
    [dispatch, leave],
  );

  const joinCall = useCallback(
    async (dialSessionId: number) => {
      dispatch(setJoining(true));
      log.info(
        `Joining a new dial session: ${dialSessionId} (existing one: ${liveListenDialSessionIdRef.current})`,
      );

      const dialSession = getDialSessionData(dialSessionId);

      if (!dialSession) {
        log.warn(`No dial session found matching ID: ${dialSessionId}`);
        dispatch(setJoining(false));
        return;
      }

      const salesfloorRoom = getSalesfloorRoomWithUserParticipating(dialSession.userId);
      if (!salesfloorRoom) {
        log.warn(
          `Could not find the salesfloor room for user: ${dialSession.userId}`,
          allRooms,
        );
        message.error('Could not join the call.');
        dispatch(setJoining(false));
        return;
      }

      if (liveListenDialSessionIdRef.current || salesfloorRoomIdRef.current) {
        log.info(
          `Leaving the existing dial session (${liveListenDialSessionIdRef.current}) before joining the next one.`,
        );
        await disconnect(liveListenDialSessionIdRef.current, salesfloorRoomIdRef.current);
      }

      const roomUrl = salesfloorRoom.videoRoom.dailyVideoRoom?.url || '';

      try {
        log.info(`Joining Salesfloor room ${salesfloorRoom.id}`);
        const {
          data: { token },
        } = await joinSalesfloorRoom(salesfloorRoom.id);

        await join(roomUrl, token, authedUser?.displayName || 'Unknown', {
          participantType: VideoParticipantType.LIVELISTEN,
        });

        dispatch(
          setLiveListenDialSessionIds({
            liveListenDialSessionId: dialSessionId,
            salesfloorRoomId: salesfloorRoom.id,
          }),
        );
        dispatch(setJoining(false));
      } catch (e) {
        dispatch(setJoining(false));
        message.error('Failed to join the call');
        log.error('Error joining room', e);
      }
    },
    [
      allRooms,
      authedUser?.displayName,
      disconnect,
      dispatch,
      getDialSessionData,
      getSalesfloorRoomWithUserParticipating,
      join,
    ],
  );

  const maybeJoinCall = useCallback(() => {
    if (autoListenState === LiveListenState.OFF) {
      return;
    }

    // check if call is active, return
    if (joining || liveListenDialSessionIdRef.current) {
      log.debug(
        `Not joining - a sesssion is already joined (${liveListenDialSessionIdRef.current})`,
      );
      return;
    }

    log.debug('Looking for joinable sessions', dialSessions);
    const joinableSessions = getJoinableSessions();
    log.debug(`Found ${joinableSessions.length} sessions`);
    if (joinableSessions.length === 0) {
      log.debug('No joinable sessions found');
      return;
    }

    const sessionToJoinIndex = getRandomInt(1, joinableSessions.length) - 1;
    const dialSession = joinableSessions[sessionToJoinIndex];
    log.info(`Will join session: ${dialSession.id}.`);

    joinCall(dialSession.id);
  }, [autoListenState, dialSessions, getJoinableSessions, joinCall, joining]);

  // Check if any of the calls should be automatically joined
  useEffect(() => {
    // Don't trigger auto-listen if the current user does not have the necessary permissions
    if (!hasLiveListenPermission) {
      log.debug('Not running auto-listen - the user has no live_listen permission');
      return;
    }
    intervalRef.current = setInterval(() => {
      maybeJoinCall();
    }, 3000);

    return () => clearInterval(intervalRef.current as NodeJS.Timeout);
  }, [hasLiveListenPermission, maybeJoinCall]);

  // When the video room changes (meaning we joined a new one) try to connect audio
  useEffect(() => {
    if (!hasLiveListenPermission) {
      log.debug('Not joining audio - the user has no live_listen permission');
      return;
    }

    if (!liveListenDialSessionId) {
      log.debug('No session ID while trying to join an audio stream');
      return;
    }
    const dialSession = getDialSessionData(liveListenDialSessionId);
    if (!dialSession) {
      log.warn(
        `No session data while trying to join audio stream: ${liveListenDialSessionId}`,
      );
      return;
    }

    const participants = [
      videoRoomManager.videoRoom?.localParticipant,
      ...(videoRoomManager.videoRoom?.remoteParticipants || []),
    ].filter(p => p) as VideoRoomParticipant[];

    if (participants && participants.length >= 2) {
      const videoRoomParticipant = participants.find(
        p => p.userId === `user_${dialSession.userId}`,
      );

      if (!videoRoomParticipant) {
        log.error(`No video participant found: user_${dialSession.userId}`);
        return;
      } else {
        if (
          audioRef.current &&
          videoRoomParticipant?.avStream &&
          audioRef.current.srcObject !== videoRoomParticipant.avStream
        ) {
          log.debug('Connecting the AV stream', videoRoomParticipant.avStream);
          audioRef.current.srcObject = videoRoomParticipant.avStream;
          audioRef.current.muted = false;

          // unmute the agent
          dispatch(
            setListenedDialingParticipantIds({
              id: `user_${dialSession.userId}`,
              listens: true,
            }),
          );
        }
      }
    }
  }, [
    dispatch,
    getDialSessionData,
    hasLiveListenPermission,
    liveListenDialSessionId,
    videoRoomManager.videoRoom,
  ]);

  const dialSessionIsActive = (dialSession: LiveListenDialSession) =>
    !dialSession.ended &&
    dialSession.tasksStarted &&
    (dialSession.startingTaskCount > dialSession.tasksStarted?.length ||
      dialSession.tasksStarted?.some(
        (dialTask: DialTask) => dialTask.status !== 'completed',
      ));

  const dialSessionHasSalesfloorRoom = useCallback(
    (dialSession: LiveListenDialSession) =>
      getSalesfloorRoomWithUserParticipating(dialSession.userId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getSalesfloorRoomWithUserParticipating, allRooms],
  );

  const activeSessions = useMemo(() => {
    if (!hasLiveListenPermission) {
      log.debug(
        'Not recalculating active sessions - the user has no live_listen permission',
      );
      return [];
    }
    log.debug('Recalculating active sessions');
    return dialSessions
      ? dialSessions
          .filter(
            (dialSession: LiveListenDialSession) =>
              dialSessionIsActive(dialSession) &&
              dialSessionHasSalesfloorRoom(dialSession),
          )
          .map((dialSession: LiveListenDialSession) => {
            const now = new Date();
            const nowUTC = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
            const agent = users.find((user: User) => user.id === dialSession.userId);
            const activeCall = dialSession.tasksStarted
              ? dialSession.tasksStarted.find(
                  (dialTask: DialTask) => dialTask.status === 'answered',
                )
              : null;
            const salesfloorRoom = getSalesfloorRoomWithUserParticipating(
              dialSession.userId,
            );

            return {
              agent,
              dialSession,
              salesfloorRoom,
              activeCall,
              prospect: activeCall?.contact,
              callStartedAt: activeCall?.dateUpdated
                ? Date.parse(activeCall?.dateUpdated)
                : null,
              callDurationSeconds: activeCall?.dateUpdated
                ? (nowUTC - Date.parse(activeCall?.dateUpdated)) / 1000
                : null,
              callStatus: activeCall?.status,
            };
          })
      : [];
  }, [
    dialSessionHasSalesfloorRoom,
    dialSessions,
    getSalesfloorRoomWithUserParticipating,
    hasLiveListenPermission,
    users,
  ]);

  const listenedSession = useMemo(() => {
    if (!liveListenDialSessionId) {
      return null;
    }
    return (
      activeSessions.find(
        session => session.dialSession.id === liveListenDialSessionId,
      ) || null
    );
  }, [activeSessions, liveListenDialSessionId]);

  return {
    activeSessions,
    listenedSession,
    audioRef,
    joinCall,
    joining,
    disconnect,
  };
};
